import React, { useCallback } from 'react'
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import styles from './PlanOverridesComponents.module.scss'
import { getFileBadge } from '../utils/fileBadge'
import { formatFileSize } from '../utils/formatFileSize'
import { useDocumentDownloadUrl } from '../hooks/useDocumentDownloadUrl'
import type { DocumentResponseDto } from '../api/dto/plan-override-document.dto'

interface UploadedCardProps {
  ruleId: string
  document: DocumentResponseDto
  readOnly?: boolean
  onDelete?: (documentId: string) => void
  ruleBase?: string
}

function UploadedCard({
  ruleId,
  document,
  readOnly,
  onDelete,
  ruleBase,
}: UploadedCardProps) {
  const { url } = useDocumentDownloadUrl(ruleId, document.id, ruleBase)
  const badge = getFileBadge(document.contentType ?? '')

  const handleDownload = useCallback(() => {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [url])

  const handleDelete = useCallback(() => {
    onDelete?.(document.id)
  }, [document.id, onDelete])

  const meta = [
    document.sizeBytes != null ? formatFileSize(document.sizeBytes) : null,
    document.createdAt
      ? new Date(document.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={styles.documentItemCard}>
      <span
        className={`${styles.documentItemBadge} ${styles[`documentItemBadge_${badge.variant}`]}`}
      >
        {badge.label}
      </span>
      <div className={styles.documentItemInfo}>
        <span className={styles.documentItemName} title={document.fileName}>
          {document.fileName}
        </span>
        {meta && <span className={styles.documentItemMeta}>{meta}</span>}
      </div>
      <div className={styles.documentItemActions}>
        <button
          type="button"
          className={styles.documentItemActionBtn}
          onClick={handleDownload}
          aria-label={`Download ${document.fileName}`}
          disabled={!url}
        >
          <DownloadOutlined />
        </button>
        {!readOnly && onDelete && (
          <button
            type="button"
            className={`${styles.documentItemActionBtn} ${styles.documentItemActionBtnDanger}`}
            onClick={handleDelete}
            aria-label={`Remove ${document.fileName}`}
          >
            <DeleteOutlined />
          </button>
        )}
      </div>
    </div>
  )
}

interface StagedCardProps {
  file: File
  onRemove: () => void
  disabled?: boolean
}

export function StagedDocumentItemCard({
  file,
  onRemove,
  disabled,
}: StagedCardProps) {
  const badge = getFileBadge(file.type)

  const handlePreview = useCallback(() => {
    const url = URL.createObjectURL(file)
    window.open(url, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }, [file])

  const handleRemove = useCallback(() => onRemove(), [onRemove])

  return (
    <div className={styles.documentItemCard}>
      <span
        className={`${styles.documentItemBadge} ${styles[`documentItemBadge_${badge.variant}`]}`}
      >
        {badge.label}
      </span>
      <div className={styles.documentItemInfo}>
        <span className={styles.documentItemName} title={file.name}>
          {file.name}
        </span>
        <span className={styles.documentItemMeta}>
          {formatFileSize(file.size)} · staged
        </span>
      </div>
      <div className={styles.documentItemActions}>
        <button
          type="button"
          className={styles.documentItemActionBtn}
          onClick={handlePreview}
          aria-label={`Preview ${file.name}`}
          disabled={disabled}
        >
          <EyeOutlined />
        </button>
        <button
          type="button"
          className={`${styles.documentItemActionBtn} ${styles.documentItemActionBtnDanger}`}
          onClick={handleRemove}
          aria-label={`Remove ${file.name}`}
          disabled={disabled}
        >
          <DeleteOutlined />
        </button>
      </div>
    </div>
  )
}

export default UploadedCard
